import {Box, Button} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type {TFunction} from "i18next";
import React, {useLayoutEffect, useRef} from "react";
import {controlRadius} from "./signingTheme";
import {DigitalSigningFailure} from "./digitalSigning.types";

interface Props {
    failure: DigitalSigningFailure;
    failedAttempts: number;
    canRetry: boolean;
    onRetry: () => void;
    t: TFunction;
}

const HAND_TO_STAFF_AFTER = 3;

function SigningSubmitError({failure, failedAttempts, canRetry, onRetry, t}: Props) {
    const theme = useTheme();
    const error = theme.palette.error.main;
    const boxRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        boxRef.current?.focus();
    }, [failedAttempts]);

    return (
        <Box
            ref={boxRef}
            role={'alert'}
            tabIndex={-1}
            sx={{
                mt: 2,
                p: 2,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: alpha(error, 0.35),
                bgcolor: alpha(error, 0.05),
                '&:focus-visible': {outline: `2px solid ${error}`, outlineOffset: 2},
            }}
        >
            <Box sx={{flex: '1 1 240px', minWidth: 0}}>
                <Typography variant={'body2'} sx={{color: error, fontWeight: 600}}>
                    {t('Your confirmation could not be sent.')}
                </Typography>
                <Typography variant={'body2'}>
                    {failure.reason === 'INVALID' ? failure.message : t('Please check the connection and try again.')}
                </Typography>
                {failedAttempts >= HAND_TO_STAFF_AFTER && (
                    <Typography variant={'body2'} sx={{mt: 1, fontWeight: 600}}>
                        {t('Please hand the tablet to staff.')}
                    </Typography>
                )}
            </Box>
            {failure.reason === 'NETWORK' && (
                <Button
                    variant={'outlined'}
                    disabled={!canRetry}
                    onClick={onRetry}
                    sx={{minHeight: 44, px: 2.5, borderRadius: `${controlRadius(theme)}px`}}
                >
                    {t('Try again')}
                </Button>
            )}
        </Box>
    );
}

export default SigningSubmitError;
